/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useState } from "react";
import { Box } from "@nordcloud/gnui";
import {
  BreadcrumbsBox,
  BrickLoader,
  DataDisplayWrapper,
  NoData,
  UniversalWrap,
} from "~/components";
import { useLexicalTabsWithUrl, useQueryState, useToggle } from "~/hooks";
import { SearchSortOrder, getFirstItem, isNil, isNotEmpty } from "~/tools";
import { CloudAccountsFilterSidebar } from "./CloudAccountList/CloudAccountsFilterSidebar";
import { CloudAccountsTabs } from "./CloudAccountList/CloudAccountsTabs";
import { staticChargeTypeOptions } from "./CloudAccountList/constants";
import { useCloudAccountsFetch, useCostsByProvider } from "./hooks";
import {
  CloudAccountsQueryState,
  Provider,
  ApplyFiltersHandler,
} from "./types";
import { useGetMovedCosts } from "./useGetMovedCosts";

const breadcrumbLabels = [{ value: "accounts", label: "Cloud Accounts" }];

export function CloudAccountsPage() {
  const { state, updateQueryState } = useQueryState<CloudAccountsQueryState>();
  const { provider, query, status } = state;

  const { data: movedCosts, loading: isLoadingMovedCosts } = useGetMovedCosts(
    query,
    status
  );

  const {
    tabs,
    loading: isLoadingAccountCounts,
    error: accountCountsError,
  } = useCostsByProvider(movedCosts);

  const {
    data,
    loading: isLoadingCloudAccounts,
    error: cloudAccountsError,
    fetchCloudAccountsVariables,
  } = useCloudAccountsFetch();

  const { activeTab, setActiveTab, registerTabs } =
    useLexicalTabsWithUrl("provider");

  useEffect(() => {
    registerTabs(tabs.map((tab) => tab.name));
    // missing params will invoke infinite loop, thus disabling exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, isLoadingAccountCounts, isLoadingMovedCosts]);

  useEffect(() => {
    if (
      !isLoadingAccountCounts &&
      !isLoadingMovedCosts &&
      isNil(provider) &&
      isNotEmpty(tabs)
    ) {
      updateQueryState({
        ...state,
        provider: getFirstItem(tabs)?.name,
      });
    }
    // missing params will invoke infinite loop, thus disabling exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, provider, isLoadingAccountCounts, isLoadingMovedCosts]);

  const [searchInput, setSearchInput] = useState(query);
  const [isFiltersSidebarOpen, toggleIsFiltersSidebarOpen] = useToggle(false);

  const error = accountCountsError || cloudAccountsError;

  const clearFilters = () => {
    updateQueryState({
      ...state,
      excludeStatus: undefined,
      excludeProvider: undefined,
      status: [],
      providerFilter: [],
      page: 0,
    });
    toggleIsFiltersSidebarOpen();
  };

  const applyFilters: ApplyFiltersHandler = useCallback(
    ({
      statusValues,
      providerValues,
      isStatusExcluded,
      isProviderExcluded,
    }) => {
      const newStatus = Object.entries(statusValues)
        .filter(([_, isChecked]) => isChecked)
        .map(([statusName]) => statusName);
      const newProviderFilterValues = Object.entries(providerValues)
        .filter(([_, isChecked]) => isChecked)
        .flatMap(([providerValue]) => {
          switch (providerValue) {
            case Provider.Aws:
            case Provider.Azure:
            case Provider.Gcp:
              return [providerValue];
            default:
              return [];
          }
        });

      updateQueryState({
        ...state,
        status: newStatus,
        providerFilter: newProviderFilterValues,
        excludeStatus: isStatusExcluded || undefined,
        excludeProvider: isProviderExcluded || undefined,
        page: 0,
      });
      toggleIsFiltersSidebarOpen();
    },
    [state, updateQueryState, toggleIsFiltersSidebarOpen]
  );

  const onTabChange = (tab: number) => {
    setActiveTab(tab, {
      chargeType: getFirstItem(staticChargeTypeOptions).value,
      order: SearchSortOrder.Dsc,
      excludeStatus: undefined,
      excludeProvider: undefined,
      page: 0,
      providerFilter: [],
      status: [],
    });
  };

  return (
    <>
      <BreadcrumbsBox title="Cloud Accounts" labels={breadcrumbLabels} />
      <UniversalWrap
        errorProps={{ error }}
        loaderProps={{
          loading: isLoadingAccountCounts || isLoadingMovedCosts,
          Component: BrickLoader,
        }}
      >
        <DataDisplayWrapper
          hasData={isNotEmpty(tabs)}
          renderNoData={(message) => (
            <Box>
              <NoData message={message} />
            </Box>
          )}
        >
          <CloudAccountsTabs
            activeTab={activeTab}
            tabs={tabs}
            openFiltersSidebar={toggleIsFiltersSidebarOpen}
            isLoadingCloudAccounts={isLoadingCloudAccounts}
            searchInput={searchInput}
            data={data}
            fetchCloudAccountsVariables={fetchCloudAccountsVariables}
            setActiveTab={onTabChange}
            onSetSearchInput={setSearchInput}
            onClear={clearFilters}
          />
        </DataDisplayWrapper>
      </UniversalWrap>
      <CloudAccountsFilterSidebar
        isFiltersSidebarOpen={isFiltersSidebarOpen}
        onClearFilters={clearFilters}
        onApplyFilters={applyFilters}
        onHandleClose={toggleIsFiltersSidebarOpen}
      />
    </>
  );
}
